// ── API Client ────────────────────────────────────────────────
// Fetch wrapper with JWT access token management and silent refresh.

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

// ── Per-tab session identity pin ──────────────────────────────
// The refresh token lives in a host-only cookie shared by *all tabs of the same
// origin*, while the access token is in-memory per tab. Without a guard, a silent
// refresh (on reload or access-token expiry) reads that shared cookie and would
// transparently switch this tab to whichever user logged in last in another tab.
// We pin the authenticated user's id in sessionStorage (which is per-tab) so a
// silent refresh that resolves to a *different* identity is refused instead of
// silently impersonating someone. Intentional logins (re)set the pin; logout and
// SSO-initiation clear it.
const SESSION_UID_KEY = 'uniticket_session_uid';

export function pinSessionUser(userId: string): void {
  try {
    sessionStorage.setItem(SESSION_UID_KEY, userId);
  } catch {
    /* sessionStorage unavailable (e.g. privacy mode) — guard degrades to a no-op */
  }
}

export function getPinnedSessionUser(): string | null {
  try {
    return sessionStorage.getItem(SESSION_UID_KEY);
  } catch {
    return null;
  }
}

export function clearPinnedSessionUser(): void {
  try {
    sessionStorage.removeItem(SESSION_UID_KEY);
  } catch {
    /* ignore */
  }
}

/** Decode (without verifying) the `sub` claim of a JWT. Verification is the server's job. */
function decodeJwtSub(token: string): string | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    let b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const json = JSON.parse(atob(b64)) as { sub?: unknown };
    return typeof json.sub === 'string' ? json.sub : null;
  } catch {
    return null;
  }
}

// Invoked when a silent refresh resolves to a different identity than this tab is
// pinned to. The auth layer registers a handler that drops to a re-login state.
let onSessionConflict: (() => void) | null = null;
export function setSessionConflictHandler(handler: (() => void) | null): void {
  onSessionConflict = handler;
}

// Invoked when a request got 401 AND the origin answered 401 to /auth/refresh —
// i.e. the shared refresh cookie no longer yields a session (expired, revoked by a
// logout / password change in another tab, or user deactivated). NOT invoked for
// identity conflicts (see above) nor for transient errors (429, 5xx, network).
let onSessionExpired: (() => void) | null = null;
export function setSessionExpiredHandler(handler: (() => void) | null): void {
  onSessionExpired = handler;
}

export const API_PREFIX = '/internal/v1';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ── 429 retry ─────────────────────────────────────────────────
// nginx's auth_limit zone refills one token every 3 s (20r/m); its 429 carries no
// Retry-After and an HTML body. express-rate-limit's 429 does carry Retry-After.
// Used only for the calls that *decide auth state* (refresh, sso/status): a
// transient 429 there must never be mistaken for "signed out". Deliberately not
// applied to ordinary apiFetch calls — a form submit should surface a 429, not
// stall for 9 s.
const RETRY_DELAYS_MS: readonly number[] = [3000, 6000];
const MAX_RETRY_AFTER_MS = 10_000;

function retryDelayMs(res: Response, attempt: number): number {
  const retryAfter = Number(res.headers.get('Retry-After'));
  if (Number.isFinite(retryAfter) && retryAfter > 0) {
    return Math.min(retryAfter * 1000, MAX_RETRY_AFTER_MS);
  }
  return RETRY_DELAYS_MS[attempt] ?? MAX_RETRY_AFTER_MS;
}

/** `fetch` that re-tries a 429 up to RETRY_DELAYS_MS.length times, then returns the last response. */
export async function fetchRetrying429(input: string, init?: RequestInit): Promise<Response> {
  let res = await fetch(input, init);
  for (let attempt = 0; res.status === 429 && attempt < RETRY_DELAYS_MS.length; attempt++) {
    await new Promise<void>((resolve) => setTimeout(resolve, retryDelayMs(res, attempt)));
    res = await fetch(input, init);
  }
  return res;
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${API_PREFIX}${path}`;

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  // Let the browser set Content-Type for FormData (with multipart boundary)
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  let res = await fetch(url, {
    ...options,
    headers,
    credentials: 'include', // send cookies (refresh token)
  });

  // 401: the access token is missing, expired or revoked. Try the shared refresh
  // cookie *whether or not this tab still holds a token* — a tab whose in-memory
  // token was dropped (failed refresh, transient error) recovers here instead of
  // 401-ing forever. If the cookie is gone too, drop the tab to re-login.
  if (res.status === 401) {
    const outcome = await silentRefresh();
    if (outcome === 'refreshed' && accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      res = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      });
    } else if (outcome === 'unauthenticated') {
      onSessionExpired?.();
    }
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }));
    throw new ApiError(res.status, body.message || res.statusText);
  }

  return res.json() as Promise<T>;
}

// ── Silent refresh ────────────────────────────────────────────

export type RefreshOutcome =
  | 'refreshed' // new access token stored
  | 'conflict' // cookie belongs to another identity than this tab's pin (handler invoked)
  | 'unauthenticated' // origin answered 401: no usable refresh cookie
  | 'error'; // 429 after retries, 5xx, network, malformed body

// Single-flight: N parallel 401s (a dashboard with several fetches and an expired
// token) share one refresh, one outcome and one handler invocation.
let refreshInFlight: Promise<RefreshOutcome> | null = null;

export function silentRefresh(): Promise<RefreshOutcome> {
  if (!refreshInFlight) {
    refreshInFlight = doSilentRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function doSilentRefresh(): Promise<RefreshOutcome> {
  let res: Response;
  try {
    res = await fetchRetrying429(`${API_PREFIX}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    accessToken = null;
    return 'error';
  }

  // The route only ever answers 200 or 401 from the app; anything else is
  // infrastructure (nginx 429/502/504) and says nothing about the session.
  if (res.status === 401) {
    accessToken = null;
    return 'unauthenticated';
  }
  if (!res.ok) {
    accessToken = null;
    return 'error';
  }

  let data: { accessToken?: unknown };
  try {
    data = await res.json();
  } catch {
    accessToken = null;
    return 'error';
  }
  if (typeof data.accessToken !== 'string') {
    accessToken = null;
    return 'error';
  }

  // Identity guard: a silent refresh must not switch this tab to another user.
  // If the refreshed token belongs to a different identity than this tab is
  // pinned to, the shared refresh cookie was overwritten by a login in another
  // tab — refuse it and let the auth layer force an explicit re-login.
  const pinned = getPinnedSessionUser();
  const sub = decodeJwtSub(data.accessToken);
  if (pinned && sub && pinned !== sub) {
    accessToken = null;
    onSessionConflict?.();
    return 'conflict';
  }

  accessToken = data.accessToken;
  return 'refreshed';
}
